const API_ORIGIN =
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3000/api'
    : '/api');

function buildUrl(path) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_ORIGIN.replace(/\/$/, '')}${normalizedPath}`;
}

export function getApiUrl(path) {
  return buildUrl(path);
}

function withTimeout(timeoutMs = 15000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return {
    signal: controller.signal,
    clear() {
      clearTimeout(timer);
    }
  };
}

function normalizeError(response, data) {
  if (typeof data === 'string') {
    const text = data.trim();
    const lower = text.toLowerCase();

    if (response.status === 502 || lower.includes('502 bad gateway')) {
      return 'Сервер временно недоступен (502 Bad Gateway)';
    }

    if (response.status === 504 || lower.includes('504 gateway timeout')) {
      return 'Сервер не ответил вовремя (504 Gateway Timeout)';
    }

    if (lower.startsWith('<html') || lower.startsWith('<!doctype html')) {
      return `Сервер вернул HTML вместо API-ответа (${response.status})`;
    }
  }

  return typeof data === 'string' ? data : data?.message;
}

export async function api(path, options = {}) {
  const timeout = withTimeout(options.timeoutMs || 15000);
  const token = localStorage.getItem('callnote_token');
  const headers = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers || {})
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    const response = await fetch(buildUrl(path), {
      ...options,
      headers,
      cache: 'no-store',
      signal: options.signal || timeout.signal
    });

    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await response.json().catch(() => ({})) : await response.text().catch(() => '');

    if (!response.ok) {
      const message = normalizeError(response, data);
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('callnote:unauthorized', {
          detail: { message: message || 'Сессия истекла. Войдите снова' }
        }));
      }
      throw new Error(message || `Request failed (${response.status})`);
    }

    return data;
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('Сервер не ответил вовремя');
    }

    if (error instanceof TypeError) {
      throw new Error('Не удалось соединиться с сервером');
    }

    throw error;
  } finally {
    timeout.clear();
  }
}

export async function uploadWithFormData(path, formData, options = {}) {
  const token = localStorage.getItem('callnote_token');
  const timeout = withTimeout(options.timeoutMs || 30000);

  try {
    const response = await fetch(buildUrl(path), {
      method: options.method || 'POST',
      body: formData,
      headers: {
        ...(options.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      cache: 'no-store',
      signal: options.signal || timeout.signal
    });

    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await response.json().catch(() => ({})) : await response.text().catch(() => '');

    if (!response.ok) {
      const message = normalizeError(response, data);
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('callnote:unauthorized', {
          detail: { message: message || 'Сессия истекла. Войдите снова' }
        }));
      }
      throw new Error(message || `Request failed (${response.status})`);
    }

    return data;
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('Сервер не ответил вовремя');
    }

    if (error instanceof TypeError) {
      throw new Error('Не удалось соединиться с сервером');
    }

    throw error;
  } finally {
    timeout.clear();
  }
}

export async function downloadApiFile(path, filenameFallback = 'download.xlsx', options = {}) {
  const timeout = withTimeout(options.timeoutMs || 60000);
  const token = localStorage.getItem('callnote_token');
  const headers = {
    ...(options.headers || {})
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    const response = await fetch(buildUrl(path), {
      ...options,
      headers,
      cache: 'no-store',
      signal: options.signal || timeout.signal
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      const data = isJson ? await response.json().catch(() => ({})) : await response.text().catch(() => '');
      const message = normalizeError(response, data);
      throw new Error(message || `Request failed (${response.status})`);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition') || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
    const filename = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1].replace(/"/g, '').trim())
      : filenameFallback;

    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  } finally {
    timeout.clear();
  }
}
